import React from 'react';

import { Alert, Flex, List, Progress } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react';

import { useAppSelector } from '../store/hooks';

interface UploadProgressAndStatusProps {
  experimentId: string;
}

export function UploadProgressAndStatus({ experimentId }: UploadProgressAndStatusProps) {
  const upload = useAppSelector((state) => state.uploads?.[experimentId]);
  if (!upload) {
    return null;
  }

  // todo: Make error/warning/success determinations more explicit from the upload state or validation results
  const hasError = Boolean(upload.error);
  const hasWarning = !hasError && Boolean(upload.validation?.issues?.some((issue) => (issue ?? '').trim().length > 0));
  const progressValue = typeof upload.progress === 'number' ? upload.progress : 0;
  const isSuccessful = !hasError && !hasWarning && upload.phase === 'completed' && progressValue === 100;
  const warningMessages = upload.validation?.issues?.filter((issue) => (issue ?? '').trim().length > 0) ?? [];
  const errorDetails = upload.errorDetails ?? [];
  const successSummary = upload.validation?.fileRows
    ? `Success! Uploaded ${upload.validation.fileRows} row${upload.validation.fileRows === 1 ? '' : 's'}.`
    : 'Success! Your antibody file passed validation.';

  return (
    <Flex direction="column" gap={4} miw="100%">
      {!isSuccessful && !hasError && !hasWarning && (
        <Progress
          data-testid="upload-progress-bar"
          value={progressValue}
          color={hasError ? 'red.6' : 'blue'}
          radius="sm"
          animated={!hasError && progressValue < 100}
        />
      )}
      {hasError && (
        <Alert data-testid="upload-error-alert" color="red" radius="sm" variant="light" icon={<IconX size={22} />}>
          Upload failed: {upload.error}
          {errorDetails.length > 0 && (
            <List size="sm" mt={4} withPadding>
              {errorDetails.map((detail) => (
                <List.Item key={detail}>{detail}</List.Item>
              ))}
            </List>
          )}
        </Alert>
      )}
      {isSuccessful && (
        <Alert data-testid="upload-success-alert" color="green" radius="sm" variant="light" icon={<IconCheck size={22} />}>
          {successSummary}
        </Alert>
      )}
      {hasWarning && !hasError && (
        <Alert data-testid="upload-warning-alert" color="yellow" radius="sm" variant="light" icon={<IconAlertTriangle size={22} />}>
          {warningMessages.length === 1 ? (
            <>
              Upload of {upload.validation?.fileRows ?? 0} rows completed with warnings: {warningMessages[0]}
            </>
          ) : (
            <>
              Upload of {upload.validation?.fileRows ?? 0} rows completed with {warningMessages.length} warnings:
              <List size="sm" mt={4} withPadding>
                {warningMessages.map((msg) => (
                  <List.Item key={msg}>{msg}</List.Item>
                ))}
              </List>
            </>
          )}
        </Alert>
      )}
    </Flex>
  );
}
